import { Component, computed, input, output, viewChild } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HmDurationPipe } from '@app/shared/pipes/hm-duration.pipe';
import { ProffeoDatePipe } from '@app/shared/pipes/proffeo-date.pipe';
import { TimeReport } from '@app/shared/types/models/reports/time-report';
import { TableColumnsEnum } from '@app/shared/types/models/reports/time-report-panel.model';
import { TimeReportRowVM } from '@app/shared/types/models/reports/time-report-row.model';
import { UiReportStatusBadgeComponent } from '@app/shared/ui/report-status-badge/ui-report-status-badge.component';
import { isTimeReportRowSelectable } from '@app/shared/utils/time-report-table.utils';
import { durationToHours } from '@app/shared/utils/time-utils';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'proffeo-reports-data-table',
  templateUrl: './reports-data-table.component.html',
  styleUrl: './reports-data-table.component.scss',
  imports: [
    MatIconModule,
    UiReportStatusBadgeComponent,
    MatTableModule,
    MatSortModule,
    MatCheckboxModule,
    MatMenuModule,
    MatTooltipModule,
    TranslatePipe,
    HmDurationPipe,
    ProffeoDatePipe
  ]
})
export class ReportsDataTableComponent {
  protected readonly TableColumnsEnum = TableColumnsEnum;
  protected readonly showInlineStatus = computed(() => !this.columns().includes(TableColumnsEnum.STATUS));
  protected readonly showStatusInType = computed(
    () => this.showInlineStatus() && !this.columns().includes(TableColumnsEnum.DATE)
  );
  protected readonly showMobileProjectTask = computed(() => !this.columns().includes(TableColumnsEnum.TASK_TITLE));
  protected readonly showCompactCommentIndicator = computed(() => !this.columns().includes(TableColumnsEnum.COMMENT));

  protected readonly dataSource = computed(() => {
    const dataSource = new MatTableDataSource<TimeReportRowVM>(this.rows());
    dataSource.sortingDataAccessor = this.sortingData;
    const sort = this.sort();
    if (sort && this.sortable()) {
      dataSource.sort = sort;
    }
    return dataSource;
  });

  protected readonly isAllSelected = computed(() => {
    const ids = this.allSelectableIds();
    const selected = this.selectedIds();
    return ids.length > 0 && ids.every(id => selected.includes(id));
  });

  protected readonly isIndeterminate = computed(() => {
    const ids = this.allSelectableIds();
    const selected = this.selectedIds();
    const count = ids.filter(id => selected.includes(id)).length;
    return count > 0 && count < ids.length;
  });

  protected sort = viewChild(MatSort);

  public readonly rows = input.required<TimeReportRowVM[]>();
  public readonly columns = input.required<TableColumnsEnum[]>();
  public readonly selectableReportIds = input<readonly string[] | null>(null);
  public readonly selectionDisabled = input(false);
  public readonly selectedIds = input<string[]>([]);
  public readonly allSelectableIds = input<string[]>([]);
  public readonly sortable = input<boolean>(false);
  public readonly showSelectAll = input<boolean>(false);

  public readonly selectReport = output<string>();
  public readonly selectAll = output<void>();
  public readonly editReport = output<TimeReport>();
  public readonly deleteReport = output<TimeReport>();
  public readonly taskDetailsOpened = output<string>();

  protected isSelected(row: TimeReportRowVM): boolean {
    const id = row.id;
    return !!id && this.selectedIds().includes(id);
  }

  protected isSelectDisabled(row: TimeReportRowVM): boolean {
    const allowed = this.selectableReportIds();
    return (
      this.selectionDisabled() ||
      (allowed === null ? !isTimeReportRowSelectable(row) : !row.id || !allowed.includes(row.id))
    );
  }

  protected onSelectReport(row: TimeReportRowVM): void {
    if (this.isSelectDisabled(row) || !row.id) {
      return;
    }
    this.selectReport.emit(row.id);
  }

  protected onSelectAll(): void {
    if (!this.selectionDisabled() && this.allSelectableIds().length) this.selectAll.emit();
  }

  protected onEditReport(row: TimeReportRowVM): void {
    this.editReport.emit(row.source);
  }

  protected onDeleteReport(row: TimeReportRowVM): void {
    this.deleteReport.emit(row.source);
  }

  protected openTaskDetails(event: Event, taskId: string | undefined): void {
    event.stopPropagation();
    if (!taskId) {
      return;
    }
    this.taskDetailsOpened.emit(taskId);
  }

  private readonly sortingData = (item: TimeReportRowVM, property: string): string | number => {
    switch (property) {
      case TableColumnsEnum.USER:
        return item.userName?.toLowerCase() || '';
      case TableColumnsEnum.COMPANY:
        return item.companyName?.toLowerCase() || '';
      case TableColumnsEnum.PROJECT:
        return item.projectName?.toLowerCase() || '';
      case TableColumnsEnum.TASK_TITLE:
        return item.task?.name?.toLowerCase() || '';
      case TableColumnsEnum.TASK_ID:
        return item.task?.taskNumber || 0;
      case TableColumnsEnum.LOCATION:
        return item.locationName?.toLowerCase() || '';
      case TableColumnsEnum.DESCRIPTION:
        return item.description?.toLowerCase() || '';
      case TableColumnsEnum.TYPE:
        return item.typeTranslationKey || '';
      case TableColumnsEnum.ESTIMATION:
        return item.estimation || 0;
      case TableColumnsEnum.TOTAL:
        return durationToHours(item.duration || '');
      case TableColumnsEnum.DATE:
        return item.date ? new Date(item.date).getTime() : 0;
      default:
        return '';
    }
  };
}
